// Facebook Tracking Parameters Utility
// Captures and manages fbclid, fbc, fbp for GHL integration

/**
 * Get Facebook Click ID (fbclid) from URL or storage
 */
export const getFbclid = () => {
  if (typeof window === 'undefined') return null;
  
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams?.get('fbclid');
  
  // Store in sessionStorage for persistence across pages
  if (fbclid) {
    sessionStorage.setItem('fbclid', fbclid);
    return fbclid;
  }
  
  // Retrieve from storage if not in URL
  return sessionStorage.getItem('fbclid');
};

/**
 * Get Facebook Browser Cookie (_fbp)
 */
export const getFbp = () => {
  if (typeof document === 'undefined') return null;
  
  const fbp = document?.cookie
    ?.split('; ')
    ?.find(row => row?.startsWith('_fbp='))
    ?.split('=')?.[1] || null;
  
  return fbp;
};

/**
 * Get or generate Facebook Click Cookie (_fbc)
 */
export const getFbc = () => {
  if (typeof document === 'undefined') return null;
  
  // Try to get existing _fbc cookie
  const fbc = document?.cookie
    ?.split('; ')
    ?.find(row => row?.startsWith('_fbc='))
    ?.split('=')?.[1] || null;
  
  if (fbc) return fbc;
  
  // Generate from fbclid if available
  const fbclid = getFbclid();
  if (fbclid) {
    return `fb.1.${Date.now()}.${fbclid}`;
  }
  
  return null;
};

/**
 * Get UTM parameters from URL or storage
 */
export const getUtmSource = () => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams?.get('utm_source');
  
  if (utmSource) {
    sessionStorage.setItem('utm_source', utmSource);
    return utmSource;
  }
  
  return sessionStorage.getItem('utm_source');
};

export const getUtmMedium = () => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmMedium = urlParams?.get('utm_medium');
  
  if (utmMedium) {
    sessionStorage.setItem('utm_medium', utmMedium);
    return utmMedium;
  }
  
  return sessionStorage.getItem('utm_medium');
};

export const getUtmCampaign = () => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmCampaign = urlParams?.get('utm_campaign');
  
  if (utmCampaign) {
    sessionStorage.setItem('utm_campaign', utmCampaign);
    return utmCampaign;
  }
  
  return sessionStorage.getItem('utm_campaign');
};

export const getUtmContent = () => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmContent = urlParams?.get('utm_content');
  
  if (utmContent) {
    sessionStorage.setItem('utm_content', utmContent);
    return utmContent;
  }
  
  return sessionStorage.getItem('utm_content');
};

export const getUtmTerm = () => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmTerm = urlParams?.get('utm_term');
  
  if (utmTerm) {
    sessionStorage.setItem('utm_term', utmTerm);
    return utmTerm;
  }
  
  return sessionStorage.getItem('utm_term');
};

/**
 * Get all UTM parameters
 */
export const getUtmParams = () => {
  return {
    utm_source: getUtmSource(),
    utm_medium: getUtmMedium(),
    utm_campaign: getUtmCampaign(),
    utm_content: getUtmContent(),
    utm_term: getUtmTerm()
  };
};

/**
 * Get all tracking parameters for GHL submission
 */
export const getTrackingParams = () => {
  return {
    fbclid: getFbclid(),
    fbp: getFbp(),
    fbc: getFbc(),
    ...getUtmParams()
  };
};

/**
 * Add fbclid to external URL (for Challenge links)
 */
export const addFbclidToUrl = (url) => {
  const fbclid = getFbclid();
  
  if (!fbclid) return url;
  
  try {
    const urlObj = new URL(url);
    urlObj?.searchParams?.set('fbclid', fbclid);
    return urlObj?.toString();
  } catch (e) {
    console.error('Invalid URL:', e);
    return url;
  }
};

/**
 * Add all Facebook tracking parameters to external URL (fbclid, fbc, fbp)
 */
export const addAllFacebookParamsToUrl = (url) => {
  const fbclid = getFbclid();
  const fbc = getFbc();
  const fbp = getFbp();
  
  try {
    const urlObj = new URL(url);
    
    if (fbclid) {
      urlObj?.searchParams?.set('fbclid', fbclid);
    }
    
    if (fbc) {
      urlObj?.searchParams?.set('fbc', fbc);
    }
    
    if (fbp) {
      urlObj?.searchParams?.set('fbp', fbp);
    }
    
    return urlObj?.toString();
  } catch (e) {
    console.error('Invalid URL:', e);
    return url;
  }
};

export default {
  getFbclid,
  getFbp,
  getFbc,
  getTrackingParams,
  addFbclidToUrl,
  addAllFacebookParamsToUrl,
  getUtmSource,
  getUtmMedium,
  getUtmCampaign,
  getUtmContent,
  getUtmTerm,
  getUtmParams
};